/**
 * Mysa Miami — scroll reveals.
 *
 * Adds `mysa-in` to anything carrying `data-mysa-reveal` once it scrolls into
 * view, which drives the angled section plates and the staggered entrances. Elements are tagged
 * here rather than in Liquid so the effect reaches sections shipped with the
 * theme without editing each one.
 *
 * Uses a throttled scroll check rather than IntersectionObserver: Chrome does
 * not deliver IO callbacks (nor run rAF) while a tab is backgrounded or
 * occluded, which left plates stuck invisible. A rect check against the
 * viewport is a handful of reads for a handful of elements and behaves the
 * same in every state.
 *
 * Degrades safely: the CSS only hides these elements under `html.js`, so
 * without JS everything renders in its final state.
 */
(function () {
  'use strict';

  var TARGETS = [
    '.mysa-anim',
    '.section-header',
    '.mysa-story__text',
    '.text-column__grid > *',
    '.mysa-track--intro .page-width',
    '.mysa-track__subheading'
  ].join(', ');

  var VISIBLE = 'mysa-in';
  var ENTER_RATIO = 0.12; // how far up the viewport an element must come
  var pending = [];
  var queued = false;

  function tag(root) {
    var found = (root || document).querySelectorAll(TARGETS);

    Array.prototype.forEach.call(found, function (el) {
      if (el.hasAttribute('data-mysa-reveal')) return;
      el.setAttribute('data-mysa-reveal', '');
      pending.push(el);
    });
  }

  function revealAll() {
    pending.forEach(function (el) {
      el.classList.add(VISIBLE);
    });
    pending = [];
  }

  function check() {
    queued = false;
    if (!pending.length) return;

    var limit = window.innerHeight * (1 - ENTER_RATIO);

    pending = pending.filter(function (el) {
      var rect = el.getBoundingClientRect();
      var onScreen = rect.top < limit && rect.bottom > 0;
      if (!onScreen) return true;
      el.classList.add(VISIBLE);
      return false;
    });

    if (!pending.length) teardown();
  }

  function schedule() {
    if (queued) return;
    queued = true;
    // setTimeout rather than requestAnimationFrame: rAF is paused while a tab
    // is hidden or occluded, which would leave plates stuck mid-reveal.
    window.setTimeout(check, 60);
  }

  function teardown() {
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
  }

  function init() {
    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    tag(document);

    if (reduced) {
      revealAll();
      return;
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    check();

    // The theme editor swaps section markup in place, so pick up new nodes.
    document.addEventListener('shopify:section:load', function (event) {
      tag(event.target);
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      schedule();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
